import { Chains, ExpiryDates, OptionChain, OptionChainResponse, OrganizeOptionDataReturn } from './entities';
import { DateTime } from 'luxon';
import { SafeError } from '@benzinga/safe-await';
import { isCloudflareChallengeResponse } from '@benzinga/session';

// The HTTP status of a refusal: 401 or an API 403. A Cloudflare challenge is also a 403, but says nothing about the user.
const getRefusalStatus = (error: SafeError | undefined): 401 | 403 | undefined => {
  const response = error?.data instanceof Response ? error.data : undefined;
  if (response?.status === 401) {
    return 401;
  }
  if (response?.status === 403 && !isCloudflareChallengeResponse(response)) {
    return 403;
  }
  return undefined;
};

/**
 * Whether the request was refused for the user (expired session or no entitlement), as opposed to failing for
 * a transient reason (5xx, timeout, network) that the next refresh may recover from.
 */
export const isOptionChainRefusal = (error: SafeError | undefined): boolean => getRefusalStatus(error) !== undefined;

/**
 * User-facing message for a failed option chain request. 401 and 403 get their own wording so a refused
 * request doesn't read as missing data.
 */
export const getOptionChainErrorMessage = (error: SafeError | undefined, symbol: string): string => {
  switch (getRefusalStatus(error)) {
    case 401:
      return 'Your session has expired. Please log in again.';
    case 403:
      return "Your subscription doesn't include Options Chain data.";
    default:
      return `Failed to load options chain for ${symbol}`;
  }
};

const formatDate = (dateString: string) => {
  const pd = DateTime.fromFormat(dateString, 'yyyyMMdd');
  return pd.toFormat('MMMM dd, yyyy');
};

export const organizeOptionData = (data: OptionChainResponse): OrganizeOptionDataReturn => {
  const dataByExpiry: { [key: string]: Chains[] } = {};

  if (Array.isArray(data.optionChains)) {
    data.optionChains?.map((chain: OptionChain) => {
      return chain.chains?.map((expiry: Chains) => {
        if (!dataByExpiry[expiry.mmy]) {
          dataByExpiry[expiry.mmy] = [];
        }

        dataByExpiry[expiry.mmy].push(expiry);
      });
    });
  }

  const expiries: ExpiryDates[] = Object.keys(dataByExpiry).map(expiry => {
    return {
      label: formatDate(expiry),
      value: expiry,
    } as ExpiryDates;
  });

  return {
    dataByExpiry: dataByExpiry,
    expiries: expiries,
  };
};
